/**
 * Registro de pagos en el dispositivo.
 *
 * Funciona igual con o sin conexion: SIEMPRE se guarda primero en local y se encola.
 * Esa es la diferencia entre una app offline-first real y una que simplemente falla
 * cuando no hay red — aqui no existe un camino "en linea" distinto que pueda
 * comportarse de otra forma.
 *
 * La validacion la hace `@senm/domain`, el MISMO modulo que valida el servidor.
 */

import type {
  ConsolidatedReceiptData,
  CreatePaymentRequest,
  ReceiptData,
} from "@senm/contracts";
import {
  autoAllocate,
  buildPayment,
  createIdGenerator,
  fromRow,
  idempotencyKey,
  motivoDescuentoAnual,
  operationalDate,
  type AllocationInput,
  type Charge,
  type MonthlyChargeState,
  type PaymentMethod,
} from "@senm/domain";
/**
 * El generador de PDF se carga BAJO DEMANDA.
 *
 * `pdf-lib` pesa más que el resto de la aplicación junta, y solo hace falta al
 * emitir o reimprimir un recibo. Cargarlo al arrancar retrasa el primer acceso de
 * la caja, que es justo el momento en que alguien está esperando.
 *
 * El service worker precachea igualmente ese trozo, así que el recibo sigue
 * generándose sin conexión: lo que cambia es CUÁNDO se descarga, no SI está.
 */
async function cargarGeneradorPdf() {
  return import("@senm/pdf");
}

/**
 * Precarga el generador en segundo plano, sin bloquear el arranque.
 *
 * Sin esto, cargarlo bajo demanda rompía el recibo PROVISIONAL: si la conexión se
 * cortaba antes de que alguien emitiera el primer recibo, el módulo nunca había
 * llegado al dispositivo y el import fallaba. Lo detectó la prueba end-to-end que
 * desconecta el navegador de verdad.
 *
 * Se llama al terminar de iniciar sesión y se ignora el fallo: si no se pudo
 * precargar, el import bajo demanda vuelve a intentarlo.
 */
export function precargarGeneradorPdf(): void {
  const lanzar = (): void => void cargarGeneradorPdf().catch(() => undefined);
  if ("requestIdleCallback" in globalThis) {
    (
      globalThis as { requestIdleCallback: (cb: () => void) => void }
    ).requestIdleCallback(lanzar);
  } else {
    setTimeout(lanzar, 0);
  }
}
import { apiDownload } from "../api/client.js";
import { db, type LocalCharge, type LocalStudent } from "../local/db.js";
import { getInstallationId, type StoredSession } from "../local/session.js";
import { enqueue } from "../sync/engine.js";

const newId = createIdGenerator();

/** Convierte los cargos locales al tipo del dominio. */
export function toDomainCharges(charges: readonly LocalCharge[]): Charge[] {
  return charges
    .filter((charge) => !charge.voided)
    .map((charge) => ({
      id: charge.id,
      studentId: charge.studentId,
      cycleId: charge.cycleId,
      conceptId: charge.conceptId,
      conceptName: charge.conceptName,
      conceptActive: charge.conceptActive,
      amount: fromRow(charge.amountMinor, charge.currency),
      applied: fromRow(charge.appliedMinor, charge.currency),
      dueDate: charge.dueDate,
    }));
}

export { autoAllocate };

export interface RegisterPaymentInput {
  session: StoredSession;
  student: LocalStudent;
  charges: readonly LocalCharge[];
  amountMinor: number;
  method: PaymentMethod;
  reference: string | null;
  allocations: AllocationInput[];
  /**
   * Estado de mora por cargo, tal como lo trajo el calendario del servidor.
   *
   * El dispositivo no guarda mora, asi que sin esto la validacion local ve solo el
   * capital y rechaza un cobro legitimo. Viene vacio cuando se cobra un importe
   * suelto sin pasar por el calendario, que es el caso sin conexion.
   */
  monthlyStates?: ReadonlyMap<string, MonthlyChargeState>;
  /**
   * Nombre del ciclo y etiqueta del mes por cargo, tomados del calendario.
   *
   * El dispositivo no los guarda: la fila local de un cargo solo sabe su concepto
   * y su importe. Sin esto el recibo salía con «Ciclo Escolar» y «Periodo» en
   * blanco, que son dos de los datos que el papá busca primero.
   */
  cycleName?: string;
  monthLabels?: ReadonlyMap<string, string>;
  /**
   * Descuento por pagar el año completo, ya repartido mes a mes.
   *
   * Se manda el PORCENTAJE al servidor y no el importe: quien decide cuánto se
   * rebaja es el colegio, no este dispositivo. El reparto que va aquí sirve para
   * dos cosas de este lado: descontar cada línea del cobro y escribir el recibo
   * provisional con la misma rebaja que imprimirá el confirmado.
   */
  annualDiscount?: {
    porcentaje: number;
    totalMinor: number;
    porCargo: ReadonlyMap<string, number>;
  } | null;
  /** Fecha con la que se extiende el comprobante. Por defecto, la del pago. */
  issuedAt?: string;
  /**
   * Fecha que se IMPRIME como «Fecha de Pago».
   *
   * Solo presentación. El pago se registra con `now`, que es lo que fija la mora y
   * la fecha operativa; esto únicamente cambia lo que se lee en el papel.
   */
  paidOn?: string;
  note: string | null;
  allowOverpayment: boolean;
  /** Instante EFECTIVO del pago: manda para la mora y para la fecha operativa. */
  now: string;
  /**
   * Instante en que se capturó, si difiere del efectivo.
   *
   * Se separan porque al registrar un pago recibido otro día el efectivo es esa
   * fecha, pero la captura es ahora, y el orden de captura es lo que desempata dos
   * cobros del mismo día.
   */
  capturedAt?: string;
}

/** Lo mínimo para entregar un PDF: los bytes y con qué nombre guardarlo. */
export interface ReciboDescargable {
  receiptBytes: Uint8Array;
  fileName: string;
}

export interface RegisterPaymentResult {
  paymentId: string;
  receipt: ReceiptData;
  receiptBytes: Uint8Array;
  fileName: string;
}

/**
 * Registra el pago localmente, emite el recibo PROVISIONAL y encola la operacion.
 *
 * El identificador del pago se genera AQUI, en el dispositivo, antes de guardar: es
 * lo que hace que un reintento posterior no pueda crear un segundo pago.
 */
export async function registerPayment(
  input: RegisterPaymentInput,
): Promise<RegisterPaymentResult> {
  const paymentId = newId();
  const estados = input.monthlyStates;
  const domainCharges = toDomainCharges(input.charges).map((cargo) => {
    const estado = estados?.get(cargo.id);
    return estado === undefined ? cargo : { ...cargo, monthlyState: estado };
  });
  const timeZone = input.session.user.timeZone;
  const currency = input.student.currency;

  // Si esto lanza, el pago NO se guarda: las mismas reglas que aplicará el servidor.
  const built = buildPayment({
    paymentId,
    institutionId: input.session.user.institutionId,
    cycleId: input.student.cycleId,
    studentId: input.student.id,
    studentActive: input.student.active,
    cycleClosed: false,
    amount: fromRow(input.amountMinor, currency),
    method: input.method,
    reference: input.reference,
    allocations: input.allocations,
    charges: domainCharges,
    effectiveAt: input.now,
    capturedAt: input.capturedAt ?? input.now,
    operationalDate: operationalDate(input.now, timeZone),
    userId: input.session.user.id,
    installationId: await getInstallationId(),
    note: input.note,
    allowOverpayment: input.allowOverpayment,
    gradeName: input.student.gradeName,
    sectionName: input.student.sectionName,
  });

  const request: CreatePaymentRequest = {
    paymentId: built.paymentId,
    studentId: built.studentId,
    cycleId: built.cycleId,
    amount: { minor: built.amount.minor, currency: currency as "GTQ" },
    method: built.method,
    reference: built.reference,
    allocations: built.allocations.map((allocation) => ({
      chargeId: allocation.chargeId,
      amount: { minor: allocation.amount.minor, currency: currency as "GTQ" },
    })),
    effectiveAt: built.effectiveAt,
    capturedAt: built.capturedAt,
    operationalDate: built.operationalDate,
    installationId: built.installationId,
    note: built.note,
    expectedStudentVersion: input.student.version,
    annualDiscount:
      input.annualDiscount == null
        ? null
        : { percent: input.annualDiscount.porcentaje },
  };

  const receipt: ReceiptData = {
    templateVersion: "1",
    institution: {
      name: input.session.user.institutionName,
      legalName: null,
      taxId: null,
      address: null,
      phone: null,
    },
    student: {
      code: input.student.code,
      fullName: input.student.fullName,
      gradeName: input.student.gradeName,
      sectionName: input.student.sectionName,
      cycleName: input.cycleName ?? "",
      // Offline no se sabe si el ciclo tiene asignación académica confirmada: se
      // deduce de lo que el dispositivo tiene sincronizado.
      assignmentPending: !input.student.gradeName || !input.student.sectionName,
    },
    payment: {
      id: built.paymentId,
      // Sin número oficial: el consecutivo lo asigna el servidor al confirmar.
      receiptNumber: null,
      status: "PENDIENTE",
      method: built.method,
      reference: built.reference,
      effectiveAt: built.effectiveAt,
      amount: { minor: built.amount.minor, currency: currency as "GTQ" },
      creditAmount: {
        minor: built.creditAmount.minor,
        currency: currency as "GTQ",
      },
      balanceBefore: {
        minor: built.studentBalanceBefore.minor,
        currency: currency as "GTQ",
      },
      balanceAfter: {
        minor: built.studentBalanceAfter.minor,
        currency: currency as "GTQ",
      },
      note: built.note,
    },
    lines: built.allocations.map((allocation) => ({
      conceptName: allocation.conceptName,
      // El dispositivo no tiene el estado de mora sincronizado, así que el recibo
      // PROVISIONAL no desglosa: muestra el importe aplicado y deja la mora en
      // blanco. El recibo CONFIRMADO, regenerado por la API, sí trae el desglose.
      cycleName: input.cycleName ?? null,
      monthLabel:
        input.monthLabels?.get(allocation.chargeId) ??
        (allocation.month === null ? null : `Mes ${allocation.month}`),
      discount: (() => {
        const rebaja = input.annualDiscount?.porCargo.get(allocation.chargeId);
        return rebaja === undefined || rebaja === 0
          ? null
          : { minor: rebaja, currency: currency as "GTQ" };
      })(),
      amount: { minor: allocation.amount.minor, currency: currency as "GTQ" },
      lateFee: null,
      capital: null,
      balanceBefore: {
        minor: allocation.balanceBefore.minor,
        currency: currency as "GTQ",
      },
      balanceAfter: {
        minor: allocation.balanceAfter.minor,
        currency: currency as "GTQ",
      },
    })),
    discount:
      input.annualDiscount == null || input.annualDiscount.totalMinor === 0
        ? null
        : {
            amount: {
              minor: input.annualDiscount.totalMinor,
              currency: currency as "GTQ",
            },
            reason: motivoDescuentoAnual(input.annualDiscount.porcentaje),
          },
    cashier: { userName: input.session.user.fullName },
    ...(input.issuedAt === undefined ? {} : { issuedAt: input.issuedAt }),
    ...(input.paidOn === undefined ? {} : { paidOn: input.paidOn }),
    timeZone,
  };

  // Todo en una transacción local: pago, cargos actualizados, saldo y cola de salida.
  // Si el navegador se cierra a mitad, no queda un pago sin encolar ni al revés.
  await db.transaction(
    "rw",
    db.payments,
    db.charges,
    db.students,
    db.outbox,
    async () => {
      await db.payments.put({
        id: built.paymentId,
        studentId: built.studentId,
        studentCode: input.student.code,
        studentName: input.student.fullName,
        cycleId: built.cycleId,
        cycleName: input.cycleName ?? "",
        gradeName: built.gradeName,
        sectionName: built.sectionName,
        amountMinor: built.amount.minor,
        currency,
        method: built.method,
        reference: built.reference,
        allocations: built.allocations.map((allocation) => ({
          chargeId: allocation.chargeId,
          conceptId: allocation.conceptId,
          conceptName: allocation.conceptName,
          monthLabel: input.monthLabels?.get(allocation.chargeId) ?? null,
          amountMinor: allocation.amount.minor,
          discountMinor:
            input.annualDiscount?.porCargo.get(allocation.chargeId) ?? null,
          balanceBeforeMinor: allocation.balanceBefore.minor,
          balanceAfterMinor: allocation.balanceAfter.minor,
        })),
        creditMinor: built.creditAmount.minor,
        status: "PENDIENTE",
        effectiveAt: built.effectiveAt,
        capturedAt: built.capturedAt,
        operationalDate: built.operationalDate,
        userId: built.userId,
        userName: input.session.user.fullName,
        installationId: built.installationId,
        note: built.note,
        balanceBeforeMinor: built.studentBalanceBefore.minor,
        balanceAfterMinor: built.studentBalanceAfter.minor,
        receiptNumber: null,
        // Se guarda para que la reimpresion desde ESTE dispositivo salga igual
        // que el recibo que se entrego: sin esto, reimprimir perderia la rebaja.
        discountMinor: input.annualDiscount?.totalMinor ?? null,
        discountReason:
          input.annualDiscount == null
            ? null
            : motivoDescuentoAnual(input.annualDiscount.porcentaje),
        statusMessage: null,
      });

      for (const allocation of built.allocations) {
        const charge = input.charges.find(
          (item) => item.id === allocation.chargeId,
        );
        if (charge === undefined) continue;
        await db.charges.update(charge.id, {
          appliedMinor: charge.appliedMinor + allocation.amount.minor,
        });
      }

      await db.students.update(input.student.id, {
        outstandingMinor: built.studentBalanceAfter.minor,
        creditMinor: input.student.creditMinor + built.creditAmount.minor,
      });

      await enqueue({
        id: built.paymentId,
        type: "PAGO",
        idempotencyKey: idempotencyKey(
          input.session.user.institutionId,
          "PAGO",
          built.paymentId,
        ),
        payload: request,
        status: "PENDIENTE",
        attempts: 0,
        createdAt: input.now,
        updatedAt: input.now,
        nextAttemptAt: null,
        dependsOn: [],
        lastErrorCode: null,
        lastErrorMessage: null,
      });
    },
  );

  const { renderReceipt, receiptFileName } = await cargarGeneradorPdf();
  const receiptBytes = await renderReceipt(receipt);
  return {
    paymentId: built.paymentId,
    receipt,
    receiptBytes,
    fileName: receiptFileName(receipt),
  };
}

/**
 * Regenera el recibo de un pago ya registrado. NO crea un pago nuevo: solo vuelve a
 * componer el PDF desde los datos guardados.
 */
/**
 * Descarga el recibo del servidor.
 *
 * Hace falta porque el historial local solo tiene los pagos cobrados EN ESTE
 * dispositivo: un pago hecho en otra caja no está en IndexedDB, y el botón
 * «Recibo» devolvía `null` y no ocurría nada —un fallo mudo, que es la peor
 * clase. El servidor guarda todos y ya sabe emitir el PDF.
 */
async function descargarReciboDelServidor(
  paymentId: string,
): Promise<ReciboDescargable> {
  const { bytes, fileName } = await apiDownload(
    `/v1/payments/${paymentId}/receipt`,
  );
  return { receiptBytes: bytes, fileName };
}

export async function reprintReceipt(
  paymentId: string,
  session: StoredSession,
): Promise<ReciboDescargable> {
  const payment = await db.payments.get(paymentId);
  // No está en este dispositivo: se pide al servidor, que los tiene todos.
  if (payment === undefined) return descargarReciboDelServidor(paymentId);

  const receipt: ReceiptData = {
    templateVersion: "1",
    institution: {
      name: session.user.institutionName,
      legalName: null,
      taxId: null,
      address: null,
      phone: null,
    },
    student: {
      code: payment.studentCode,
      fullName: payment.studentName,
      gradeName: payment.gradeName,
      sectionName: payment.sectionName,
      cycleName: "",
      assignmentPending: !payment.gradeName || !payment.sectionName,
    },
    payment: {
      id: payment.id,
      receiptNumber: payment.receiptNumber,
      status: payment.status as ReceiptData["payment"]["status"],
      method: payment.method as ReceiptData["payment"]["method"],
      reference: payment.reference,
      effectiveAt: payment.effectiveAt,
      amount: {
        minor: payment.amountMinor,
        currency: payment.currency as "GTQ",
      },
      creditAmount: {
        minor: payment.creditMinor,
        currency: payment.currency as "GTQ",
      },
      balanceBefore: {
        minor: payment.balanceBeforeMinor,
        currency: payment.currency as "GTQ",
      },
      balanceAfter: {
        minor: payment.balanceAfterMinor,
        currency: payment.currency as "GTQ",
      },
      note: payment.note,
    },
    lines: payment.allocations.map((allocation) => ({
      conceptName: allocation.conceptName,
      // La reimpresión local reproduce lo que el dispositivo guardó. Si el pago ya
      // se confirmó, el desglose autoritativo está en el recibo que regenera la API.
      cycleName: payment.cycleName ?? null,
      monthLabel: allocation.monthLabel ?? null,
      lateFee: null,
      capital: null,
      discount:
        allocation.discountMinor == null || allocation.discountMinor === 0
          ? null
          : {
              minor: allocation.discountMinor,
              currency: payment.currency as "GTQ",
            },
      amount: {
        minor: allocation.amountMinor,
        currency: payment.currency as "GTQ",
      },
      balanceBefore: {
        minor: allocation.balanceBeforeMinor,
        currency: payment.currency as "GTQ",
      },
      balanceAfter: {
        minor: allocation.balanceAfterMinor,
        currency: payment.currency as "GTQ",
      },
    })),
    discount:
      payment.discountMinor == null || payment.discountMinor === 0
        ? null
        : {
            amount: {
              minor: payment.discountMinor,
              currency: payment.currency as "GTQ",
            },
            reason: payment.discountReason ?? "Descuento por pago anual",
          },
    cashier: { userName: payment.userName },
    timeZone: session.user.timeZone,
  };

  const { renderReceipt, receiptFileName } = await cargarGeneradorPdf();
  const receiptBytes = await renderReceipt(receipt);
  return { receiptBytes, fileName: receiptFileName(receipt) };
}

/** Abre el PDF en una pestana nueva para verlo, imprimirlo o guardarlo. */
export function openPdf(bytes: Uint8Array, fileName: string): void {
  const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Se libera despues de que el navegador tomó el blob.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/**
 * Comprobante consolidado PROVISIONAL, generado en el dispositivo.
 *
 * El de «Comprobante consolidado» lo produce el servidor, y hace bien: ahí se
 * reimprimen pagos viejos que el dispositivo quizá ya no tiene. Pero justo
 * después de cobrar a varios hermanos, esos pagos todavía NO han llegado al
 * servidor —nacen `PENDIENTE` y suben en la siguiente sincronización—, así que
 * pedírselo devolvía 404 y caja se quedaba sin papel con el dinero ya recibido.
 *
 * Se arma con los mismos `ReceiptData` que devolvió cada cobro, que es
 * exactamente lo que el recibo individual ya usa para imprimirse sin conexión.
 */
export async function renderConsolidatedLocalReceipt(
  recibos: readonly ReceiptData[],
): Promise<ReciboDescargable> {
  const primero = recibos[0];
  if (primero === undefined) {
    throw new Error("No hay ningún cobro que consolidar.");
  }

  const data: ConsolidatedReceiptData = {
    templateVersion: "1",
    institution: primero.institution,
    entries: recibos.map((recibo) => ({
      student: recibo.student,
      payment: recibo.payment,
      lines: recibo.lines,
      discount: recibo.discount,
    })),
    cashier: primero.cashier,
    timeZone: primero.timeZone,
    ...(primero.issuedAt === undefined ? {} : { issuedAt: primero.issuedAt }),
    ...(primero.paidOn === undefined ? {} : { paidOn: primero.paidOn }),
  };

  const pdf = await cargarGeneradorPdf();
  return {
    receiptBytes: await pdf.renderConsolidatedReceipt(data),
    fileName: pdf.consolidatedReceiptFileName(data),
  };
}
